/**
 * 変更履歴ページ (監査ログ)
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business creative_audit_logs の閲覧画面。誰が・いつ・何を変更したかを before/after 展開付きで表示。
 * @graph-connects api [calls] audit.list による監査ログ取得
 */
import { CreativeEntityTypeSchema, type CreativeEntityType } from '@cortex/domain-creative/web';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { formatDateTime } from '../lib/labels.js';
import { EmptyState, GhostButton, INPUT_CLASS, SectionHeading } from '../components/ui.js';

/** 1 ページの取得件数。 @graph-connects none */
const PAGE_SIZE = 50;

/**
 * 変更履歴ページ。
 * @returns ページ要素
 * @graph-connects api [calls] audit.list による監査ログ取得
 */
export function HistoryPage(): JSX.Element {
  const [entityType, setEntityType] = useState('');
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const auditQuery = useQuery(
    orpc.audit.list.queryOptions({
      input: {
        entityType: entityType === '' ? undefined : (entityType as CreativeEntityType),
        limit: PAGE_SIZE,
        offset,
      },
    }),
  );
  const items = auditQuery.data?.items ?? [];
  const hasMore = auditQuery.data?.hasMore ?? false;

  return (
    <div>
      <SectionHeading title="History" />
      <div className="mb-4">
        <select
          className={`${INPUT_CLASS} w-auto`}
          value={entityType}
          onChange={(event) => {
            setEntityType(event.target.value);
            setOffset(0);
          }}
        >
          <option value="">全エンティティ</option>
          {CreativeEntityTypeSchema.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <EmptyState message="変更履歴がありません" />
      ) : (
        <div className="dh-table-frame">
          <table className="w-full text-body">
            <thead>
              <tr>
                {['日時', '変更者', 'アクション', '対象', ''].map((header, index) => (
                  <th key={String(index)} className="dh-th">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => (
                <Fragment key={entry.id}>
                  <tr className="hover:bg-glass">
                    <td className="dh-td font-mono text-xs text-muted">
                      {formatDateTime(entry.changedAt)}
                    </td>
                    <td className="dh-td text-subtle">{entry.changedBy}</td>
                    <td className="dh-td">
                      <span className="rounded bg-surface px-1.5 py-0.5 font-mono text-caption text-subtle">
                        {entry.action}
                      </span>
                    </td>
                    <td className="dh-td text-subtle">
                      <span className="font-mono text-xs">
                        {entry.entityType}/{entry.entityId}
                      </span>
                    </td>
                    <td className="dh-td text-right">
                      <button
                        type="button"
                        className="text-xs text-muted hover:text-foreground"
                        onClick={() => {
                          setExpandedId(expandedId === entry.id ? null : entry.id);
                        }}
                      >
                        {expandedId === entry.id ? '閉じる' : 'diff'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === entry.id && (
                    <tr>
                      <td colSpan={5} className="border-b border-line-light px-3.5 py-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="mb-1 text-micro font-bold uppercase text-muted">
                              Before
                            </div>
                            <pre className="max-h-64 overflow-auto rounded-lg bg-surface p-3 font-mono text-micro text-subtle">
                              {entry.before === null
                                ? '(なし)'
                                : JSON.stringify(entry.before, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <div className="mb-1 text-micro font-bold uppercase text-muted">
                              After
                            </div>
                            <pre className="max-h-64 overflow-auto rounded-lg bg-surface p-3 font-mono text-micro text-subtle">
                              {entry.after === null
                                ? '(なし)'
                                : JSON.stringify(entry.after, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-5 flex justify-between">
        {offset > 0 ? (
          <GhostButton
            onClick={() => {
              setOffset(Math.max(0, offset - PAGE_SIZE));
            }}
          >
            ← 前へ
          </GhostButton>
        ) : (
          <span />
        )}
        {hasMore && (
          <GhostButton
            onClick={() => {
              setOffset(offset + PAGE_SIZE);
            }}
          >
            次へ →
          </GhostButton>
        )}
      </div>
    </div>
  );
}
